/**
 * Organization Storage Hook
 * Manages state for organization's connected blob storage
 * Automatically enables file upload when storage is connected
 */

import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import { storageService, StorageInfo, BlobInfo } from '../services/storage'

// File status for upload tracking
export type StorageFileStatus = 'pending' | 'uploading' | 'completed' | 'error'

// Uploaded file representation
export interface StorageFile {
  id: string
  name: string
  size: number
  type: string
  status: StorageFileStatus
  progress: number
  error?: string
  uploadedAt?: Date
  // Storage-specific fields
  blobUrl?: string
  container?: string
  path?: string
}

// Store state interface
interface OrgStorageState {
  // Storage availability
  isConnected: boolean
  isCheckingConnection: boolean
  storageInfo: StorageInfo | null
  connectionError: string | null

  // File management
  files: StorageFile[]
  isUploading: boolean
  uploadError: string | null

  // Blob listing
  blobs: BlobInfo[]
  isLoadingBlobs: boolean

  // Actions
  checkStorageConnection: () => Promise<boolean>
  uploadFiles: (files: File[]) => Promise<StorageFile[]>
  uploadFile: (file: File, path?: string) => Promise<StorageFile>
  removeFile: (fileId: string) => void
  clearFiles: () => void
  loadBlobs: (prefix?: string) => Promise<void>
  deleteBlob: (container: string, path: string) => Promise<void>
  setError: (error: string | null) => void
}

export const useOrgStorageStore = create<OrgStorageState>()(
  subscribeWithSelector((set, get) => ({
    // Initial state
    isConnected: false,
    isCheckingConnection: false,
    storageInfo: null,
    connectionError: null,
    files: [],
    isUploading: false,
    uploadError: null,
    blobs: [],
    isLoadingBlobs: false,

    /**
     * Check if the organization has connected storage
     * Call this on app init to determine if file upload should be enabled
     */
    checkStorageConnection: async (): Promise<boolean> => {
      set({ isCheckingConnection: true, connectionError: null })

      try {
        const info = await storageService.getStorageInfo()

        set({
          isConnected: info.connected,
          storageInfo: info,
          isCheckingConnection: false,
        })

        if (info.connected) {
          console.log('[OrgStorage] Storage connected:', info.account_name)
        } else {
          console.log('[OrgStorage] No storage connected for organization')
        }

        return info.connected
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to check storage'
        console.error('[OrgStorage] Connection check failed:', errorMessage)

        set({
          isConnected: false,
          storageInfo: null,
          connectionError: errorMessage,
          isCheckingConnection: false,
        })

        return false
      }
    },

    /**
     * Upload multiple files to organization storage
     */
    uploadFiles: async (filesToUpload: File[]): Promise<StorageFile[]> => {
      const { isConnected } = get()

      if (!isConnected) {
        throw new Error('Storage not connected')
      }

      set({ isUploading: true, uploadError: null })

      // Create file records with pending status
      const storageFiles: StorageFile[] = filesToUpload.map((file) => ({
        id: crypto.randomUUID(),
        name: file.name,
        size: file.size,
        type: file.type || 'application/octet-stream',
        status: 'pending' as StorageFileStatus,
        progress: 0,
      }))

      // Add to state immediately for UI feedback
      set((state) => ({
        files: [...state.files, ...storageFiles],
      }))

      const results: StorageFile[] = []

      for (let i = 0; i < filesToUpload.length; i++) {
        const file = filesToUpload[i]!
        const storageFile = storageFiles[i]!

        // Update status to uploading
        set((state) => ({
          files: state.files.map((f) =>
            f.id === storageFile.id
              ? { ...f, status: 'uploading' as StorageFileStatus, progress: 10 }
              : f
          ),
        }))

        try {
          // Generate path: uploads/{timestamp}-{filename}
          const path = `uploads/${Date.now()}-${file.name}`

          // Simulate progress updates
          set((state) => ({
            files: state.files.map((f) =>
              f.id === storageFile.id ? { ...f, progress: 50 } : f
            ),
          }))

          const response = await storageService.uploadFile(file, { path })

          // Update to completed
          const completedFile: StorageFile = {
            id: storageFile.id,
            name: storageFile.name,
            size: storageFile.size,
            type: storageFile.type,
            status: 'completed',
            progress: 100,
            uploadedAt: new Date(),
            blobUrl: response.blob_url,
            container: response.container,
            path: response.path,
          }

          set((state) => ({
            files: state.files.map((f) =>
              f.id === storageFile.id ? completedFile : f
            ),
          }))

          results.push(completedFile)
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Upload failed'

          // Update to error status
          set((state) => ({
            files: state.files.map((f) =>
              f.id === storageFile.id
                ? { ...f, status: 'error' as StorageFileStatus, error: errorMessage }
                : f
            ),
          }))

          results.push({
            id: storageFile.id,
            name: storageFile.name,
            size: storageFile.size,
            type: storageFile.type,
            status: 'error',
            progress: 0,
            error: errorMessage,
          })
        }
      }

      set({ isUploading: false })
      return results
    },

    /**
     * Upload a single file
     */
    uploadFile: async (file: File, _customPath?: string): Promise<StorageFile> => {
      const results = await get().uploadFiles([file])
      return results[0]!
    },

    /**
     * Remove a file from the local list (does not delete from storage)
     */
    removeFile: (fileId: string) => {
      set((state) => ({
        files: state.files.filter((f) => f.id !== fileId),
      }))
    },

    /**
     * Clear all files from local state
     */
    clearFiles: () => {
      set({ files: [], uploadError: null })
    },

    /**
     * Load blobs from storage
     */
    loadBlobs: async (prefix?: string): Promise<void> => {
      const { isConnected } = get()

      if (!isConnected) {
        return
      }

      set({ isLoadingBlobs: true })

      try {
        const response = await storageService.listFiles({ prefix })
        set({ blobs: response.blobs, isLoadingBlobs: false })
      } catch (error) {
        console.error('[OrgStorage] Failed to load blobs:', error)
        set({ blobs: [], isLoadingBlobs: false })
      }
    },

    /**
     * Delete a blob from storage
     */
    deleteBlob: async (container: string, path: string): Promise<void> => {
      try {
        await storageService.deleteFile(container, path)
        // Reload blobs after deletion
        await get().loadBlobs()
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Delete failed'
        set({ uploadError: errorMessage })
        throw error
      }
    },

    /**
     * Set error message
     */
    setError: (error: string | null) => {
      set({ uploadError: error })
    },
  }))
)

/**
 * Hook to use organization storage
 * Automatically checks connection on first use
 */
export function useOrgStorage() {
  const store = useOrgStorageStore()
  return store
}

export default useOrgStorage
